<template>
  <loader :is-loading="isApplicationLoading" />
  <lf-card v-if="!isApplicationLoading" class="mb-8 py-6">
    <template #header>
      <div class="flex w-full flex-col">
        <div class="flex items-start justify-between gap-40">
          <div class="flex items-center">
            <lf-h3>
              {{ $t("DEALS.DEAL_PROGRESS.SIGNED.TITLE") }}
            </lf-h3>
            <div
              class="mt-0-5 ml-2 flex h-5 w-5 items-center justify-center rounded-full bg-blue-100"
            >
              <span class="-mt-0-25 font-medium text-blue-500">
                {{ numOfSignedDeals }}
              </span>
            </div>
          </div>
          <div
            v-if="numOfSignedDeals || chosenProductType"
            class="w-max-200 flex flex-wrap justify-end gap-3"
          >
            <div class="flex overflow-hidden rounded-sm border">
              <button
                class="inline-flex cursor-pointer items-center justify-center border-r bg-white px-4 py-2"
                @click.prevent="setShowList(false)"
              >
                <icon-base
                  :icon="IconFullscreen"
                  :icon-color="!showList ? UTIL_COLORS.PRIMARY : 'lightgray'"
                  height="16"
                  width="20"
                  viewBox="0 0 16 18"
                />
              </button>
              <button
                class="mr-px inline-flex cursor-pointer items-center justify-center bg-white px-4 py-2"
                @click.prevent="setShowList(true)"
              >
                <icon-base
                  :icon="IconShowList"
                  :icon-color="!showList ? 'lightgray' : UTIL_COLORS.PRIMARY"
                  height="16"
                  width="19"
                />
              </button>
            </div>
            <lf-dropdown
              class="w-60 max-w-60"
              name="product_type"
              :value="chosenProductType"
              :options="productTypes"
              :placeholder="$t('DEALS.DOCUMENTS.CHOOSE_PRODUCT_TYPE')"
              full-width
              show-clear-filter
              @change="handleProductTypeChange"
            />
            <primary-button
              v-if="canEditDealApplicationAndUnderwriting"
              @click.prevent="isDocumentModalOpen = true"
            >
              {{ $t("DEALS.DOCUMENTS.ADD_APPLICATIONS") }}
            </primary-button>
          </div>
        </div>
        <lf-scrollable-menu
          class="mt-8 flex w-full"
          :styling-options="{
            offsetRightClasses: 'top-0 right-0',
            offsetLeftClasses: 'top-0 left-0',
          }"
        >
          <div
            v-if="primaryOwnerAndOthers?.length > 1"
            class="flex h-10 cursor-pointer items-center rounded-sm px-4 py-2"
            :class="currentOwner ? 'border' : 'bg-blue-100'"
            @click="setOwner(null)"
          >
            <icon-base
              :icon="IconOwnerSmall"
              height="10"
              width="10"
              :icon-color="currentOwner ? 'gray' : UTIL_COLORS.PRIMARY"
              class="mr-3"
            />
            <span
              class="text-xs font-semibold"
              :class="{
                'text-blue-600': !currentOwner,
                'text-gray-500': currentOwner,
              }"
            >
              {{ $t("COMMON.ALL_OWNERS") }}
            </span>
          </div>

          <select-owner-card
            v-for="(owner, index) in primaryOwnerAndOthers"
            :key="owner.id"
            :index="index"
            :owner="owner"
            :is-selected="currentOwner?.id === owner.id"
            class="ml-4"
            @click="setOwner(owner)"
          />
        </lf-scrollable-menu>
      </div>
      <document-upload-modal
        v-if="isDocumentModalOpen"
        :close="() => (isDocumentModalOpen = false)"
        :owner-id="currentOwner ? String(currentOwner?.id) : ''"
        signed-doc-only
      />
    </template>
    <signed-deal
      :personal-info="currentOwner"
      :product-type="chosenProductType"
      :show-list="showList"
      :open-modal="() => (isDocumentModalOpen = true)"
    />
  </lf-card>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";

import { SIGNED_DOC_FILE_TYPE, UTIL_COLORS } from "@/helpers/constants";
import { useDealFiles, useDealOwners, useDeals } from "@/hooks/deals";

import SelectOwnerCard from "./SelectOwnerCard.vue";
import SignedDeal from "./SignedDeal.vue";
import DocumentUploadModal from "@/components/deals/documents/DocumentUploadModal.vue";
import IconFullscreen from "@/components/icons/IconFullscreen.vue";
import IconOwnerSmall from "@/components/icons/IconOwnerSmall.vue";
import IconShowList from "@/components/icons/IconShowList.vue";
import LfScrollableMenu from "@/components/ui/LfScrollableMenu.vue";

import type { ProductType } from "@/models/options";
import type { IOwner, ISignedDoc } from "@/models/users";

const { getters } = useStore();
const { activeDeal, canEditDealApplicationAndUnderwriting } = useDeals();
const primaryOwnerAndOthers = useDealOwners();
const { files, getDealFiles } = useDealFiles(activeDeal.value.id);

const isDocumentModalOpen = ref(false);
const chosenProductType = ref<ProductType | undefined>(undefined);

const isApplicationLoading = computed<boolean>(
  () => getters["applications/loading"]
);

const currentOwner = ref<IOwner | null>(
  primaryOwnerAndOthers.value?.length > 1
    ? null
    : primaryOwnerAndOthers.value?.[0]
);

const showList = ref(false);

const currentOwnerNumOfDocs = computed(
  () =>
    currentOwner.value?.signed_docs?.filter((doc) => {
      if (chosenProductType.value) {
        return (
          doc.product_id === Number(chosenProductType.value) &&
          doc.signature_status === 2
        );
      }
      return doc && doc.signature_status === 2;
    }).length || 0
);

const allOwnersNumOfDocs = computed(
  () =>
    files.value.filter((file) => {
      if (chosenProductType.value) {
        return (
          file.product_id === Number(chosenProductType.value) &&
          file.file_type === SIGNED_DOC_FILE_TYPE
        );
      }
      return file.file_type === SIGNED_DOC_FILE_TYPE;
    }).length || 0
);

const numOfSignedDeals = computed(() =>
  currentOwner.value ? currentOwnerNumOfDocs.value : allOwnersNumOfDocs.value
);

const productTypes = computed<Record<string, string>>(() => {
  const allProductTypes = getters["options/productTypes"];

  const allSignedDocs = primaryOwnerAndOthers.value?.reduce(
    (acc, owner) => [
      ...acc,
      ...(owner.signed_docs?.filter((doc) => doc.signature_status === 2) || []),
    ],
    [] as ISignedDoc[]
  );

  const signedDocs =
    currentOwner.value?.signed_docs?.filter(
      (doc) => doc.signature_status === 2
    ) || allSignedDocs;

  const formattedProductTypes = Object.keys(allProductTypes).reduce(
    (acc, curr) => {
      const signedDocsByProductTypeNumber = signedDocs?.filter(
        (doc) => doc.product_id === Number(curr)
      ).length;

      return {
        ...acc,
        [curr]: allProductTypes[curr] + ` (${signedDocsByProductTypeNumber})`,
      };
    },
    {}
  );

  return formattedProductTypes;
});

watch(primaryOwnerAndOthers, () => {
  if (primaryOwnerAndOthers.value?.length > 1) {
    currentOwner.value = null;
    return;
  }
  currentOwner.value = primaryOwnerAndOthers.value?.[0];
});

const setShowList = (val: boolean) => {
  showList.value = val;
};

const setOwner = (selectedOwner: IOwner | null) => {
  if (currentOwner.value?.id === selectedOwner?.id) {
    return;
  }
  currentOwner.value = selectedOwner;
  chosenProductType.value = undefined;
};

const handleProductTypeChange = (option: ProductType) => {
  chosenProductType.value = option;
};

onMounted(getDealFiles);
</script>
